import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const STORAGE_PREFIX = 'crmllor:filters';
const AGENT_KEY = 'crmllor:agentId';

// Identificador estable del navegador (el "agente"): un UUID persistido en
// localStorage que se envía al backend. Cada navegador conserva sus propias
// preferencias de listado (patrón usuario + navegador + pantalla).
function getAgentId() {
  try {
    let id = window.localStorage.getItem(AGENT_KEY);
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `a-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(AGENT_KEY, id);
    }
    return id;
  } catch {
    return 'no-agent';
  }
}

const readStored = (key, fallback) => {
  if (!key || typeof window === 'undefined') return fallback;
  const raw = window.localStorage.getItem(key);
  if (raw == null) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
};

// Preferencia de listado persistida por usuario + navegador + pantalla.
// Fuente de verdad: BD (GET/PUT /user-prefs). localStorage es solo caché para
// pintar al instante sin esperar a la red. La API es idéntica a useState, por
// eso las pantallas que ya lo usan no cambian.
const useUserPreference = (pageKey, initialValue) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const fullKey = userId ? `${STORAGE_PREFIX}:u${userId}:${pageKey}` : null;
  const initialRef = useRef(initialValue);

  const [value, setValueState] = useState(() => readStored(fullKey, initialRef.current));
  const valueRef = useRef(value);
  valueRef.current = value;
  const keyRef = useRef(fullKey);

  // Al cambiar de usuario/pantalla: pinta la caché al instante y luego trae de
  // BD (si responde, manda BD). Si la red falla, se queda la caché local. Esta
  // hidratación NO re-persiste (no dispara PUT).
  useEffect(() => {
    keyRef.current = fullKey;
    if (!fullKey) { setValueState(initialRef.current); return undefined; }
    setValueState(readStored(fullKey, initialRef.current));
    let cancelled = false;
    api.get('/user-prefs', { params: { agentId: getAgentId(), pageKey } })
      .then((res) => {
        if (cancelled || keyRef.current !== fullKey) return;
        if (res.data && res.data.value != null) {
          setValueState(res.data.value);
          try { window.localStorage.setItem(fullKey, JSON.stringify(res.data.value)); } catch {}
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [fullKey, pageKey]);

  // setValue público (lo llaman las pantallas): actualiza estado + caché + BD.
  const setValue = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(valueRef.current) : next;
    setValueState(resolved);
    if (!fullKey) return;
    try { window.localStorage.setItem(fullKey, JSON.stringify(resolved)); } catch {}
    api.put('/user-prefs', { agentId: getAgentId(), pageKey, value: resolved }).catch(() => {});
  }, [fullKey, pageKey]);

  return [value, setValue];
};

export default useUserPreference;
